import { useCallback, useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { areAttachmentsEquivalent } from 'UI/utils/attachments';

const AUTOSAVE_DELAY_MS = 3000;

const normalizeEmpty = value => (value === '' || value === null ? undefined : value);

const buildComparableSnapshot = (snapshot, other) => {
  return Object.keys(snapshot).reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(other, key)) {
      acc[key] = normalizeEmpty(snapshot[key]);
    }
    return acc;
  }, {});
};

const snapshotsAreEqual = (snapshot, baselineSnapshot) => {
  if (!baselineSnapshot) return true;

  const snapshotAttachments = snapshot.attachments ?? [];
  const baselineAttachments = baselineSnapshot.attachments ?? [];
  if (!areAttachmentsEquivalent(snapshotAttachments, baselineAttachments)) return false;

  const { attachments: _sa, ...snapshotFields } = snapshot;
  const { attachments: _ba, ...baselineFields } = baselineSnapshot;
  const comparableBaseline = buildComparableSnapshot(baselineFields, snapshotFields);
  const comparableSnapshot = buildComparableSnapshot(snapshotFields, baselineFields);

  return isEqual(comparableBaseline, comparableSnapshot);
};

export const useAutosaveDraft = ({
  attachments,
  autosaveValues = null,
  autosaveOnReadyKey = null,
  canSaveDraft,
  getFormValues,
  getPendingSavePromise = null,
  lastSavedSnapshotRef,
  saveDraft
}) => {
  const canSaveDraftRef = useRef(canSaveDraft);
  const saveDraftRef = useRef(saveDraft);
  const getPendingSavePromiseRef = useRef(getPendingSavePromise);
  const attachmentsRef = useRef(attachments);
  const baselineSnapshotRef = useRef(null);
  const previousCanSaveDraftRef = useRef(canSaveDraft);
  const queuedReadyKeysRef = useRef(new Set());
  const inFlightReadyKeysRef = useRef(new Set());
  const autosavedReadyKeysRef = useRef(new Set());
  const observedSnapshotRef = useRef(null);

  canSaveDraftRef.current = canSaveDraft;
  saveDraftRef.current = saveDraft;
  getPendingSavePromiseRef.current = getPendingSavePromise;
  attachmentsRef.current = attachments;

  const debouncedSave = useRef(
    debounce((formValues, currentAttachments, readyKey = null) => {
      if (!canSaveDraftRef.current) {
        if (readyKey) queuedReadyKeysRef.current.delete(readyKey);
        return Promise.resolve(null);
      }

      if (readyKey) {
        queuedReadyKeysRef.current.delete(readyKey);
        inFlightReadyKeysRef.current.add(readyKey);
      }

      const savePromise = saveDraftRef.current(
        { ...formValues, attachments: currentAttachments },
        { silent: true }
      );

      if (!readyKey) return savePromise;

      return Promise.resolve(savePromise)
        .then(result => {
          if (result) {
            autosavedReadyKeysRef.current.add(readyKey);
          }
          return result;
        })
        .catch(error => {
          autosavedReadyKeysRef.current.delete(readyKey);
          throw error;
        })
        .finally(() => {
          inFlightReadyKeysRef.current.delete(readyKey);
        });
    }, AUTOSAVE_DELAY_MS)
  ).current;

  useEffect(() => {
    return () => debouncedSave.cancel();
  }, [debouncedSave]);

  useEffect(() => {
    if (canSaveDraft) return;

    queuedReadyKeysRef.current.clear();
    debouncedSave.cancel();
  }, [canSaveDraft, debouncedSave]);

  const captureBaseline = useCallback(
    baselineSnapshot => {
      baselineSnapshotRef.current = baselineSnapshot || {
        ...getFormValues(),
        attachments: attachmentsRef.current
      };
    },
    [getFormValues]
  );

  useEffect(() => {
    if (baselineSnapshotRef.current) return undefined;

    const rafId = requestAnimationFrame(() => {
      captureBaseline();
    });

    return () => cancelAnimationFrame(rafId);
  }, [captureBaseline]);

  const hasSnapshotChanged = useCallback(
    snapshot => {
      const baselineSnapshot = lastSavedSnapshotRef.current || baselineSnapshotRef.current;

      return !snapshotsAreEqual(snapshot, baselineSnapshot);
    },
    [lastSavedSnapshotRef]
  );

  const hasUnsavedChanges = useCallback(() => {
    const baselineSnapshot = lastSavedSnapshotRef.current || baselineSnapshotRef.current;

    if (!baselineSnapshot) return false;

    const currentSnapshot = {
      ...getFormValues(),
      attachments: attachmentsRef.current
    };

    return hasSnapshotChanged(currentSnapshot);
  }, [lastSavedSnapshotRef, getFormValues, hasSnapshotChanged]);

  const triggerAutosave = useCallback(
    (formValues, currentAttachments) => {
      if (!canSaveDraftRef.current) return;

      const snapshot = {
        ...formValues,
        attachments: currentAttachments
      };

      if (!hasSnapshotChanged(snapshot)) {
        debouncedSave.cancel();
        return;
      }

      debouncedSave(formValues, currentAttachments);
    },
    [debouncedSave, hasSnapshotChanged]
  );

  useEffect(() => {
    if (!autosaveValues) return;

    const snapshot = { ...autosaveValues, attachments };
    if (isEqual(snapshot, observedSnapshotRef.current)) return;

    observedSnapshotRef.current = snapshot;
    triggerAutosave(autosaveValues, attachments);
  }, [attachments, autosaveValues, triggerAutosave]);

  useEffect(() => {
    const wasAbleToSaveDraft = previousCanSaveDraftRef.current;

    if (!wasAbleToSaveDraft && canSaveDraft && hasUnsavedChanges()) {
      debouncedSave(getFormValues(), attachmentsRef.current);
    }

    previousCanSaveDraftRef.current = canSaveDraft;
  }, [canSaveDraft, debouncedSave, getFormValues, hasUnsavedChanges]);

  useEffect(() => {
    if (
      !autosaveOnReadyKey ||
      !canSaveDraft ||
      queuedReadyKeysRef.current.has(autosaveOnReadyKey) ||
      inFlightReadyKeysRef.current.has(autosaveOnReadyKey) ||
      autosavedReadyKeysRef.current.has(autosaveOnReadyKey)
    ) {
      return undefined;
    }

    const rafId = requestAnimationFrame(() => {
      if (
        !canSaveDraftRef.current ||
        queuedReadyKeysRef.current.has(autosaveOnReadyKey) ||
        inFlightReadyKeysRef.current.has(autosaveOnReadyKey) ||
        autosavedReadyKeysRef.current.has(autosaveOnReadyKey)
      ) {
        return;
      }

      queuedReadyKeysRef.current.add(autosaveOnReadyKey);
      debouncedSave(getFormValues(), attachmentsRef.current, autosaveOnReadyKey);
    });

    return () => cancelAnimationFrame(rafId);
  }, [autosaveOnReadyKey, canSaveDraft, debouncedSave, getFormValues]);

  const flushAutosave = useCallback(() => {
    const flushedResult = debouncedSave.flush();
    const pendingSavePromise = getPendingSavePromiseRef.current?.();

    return pendingSavePromise ?? flushedResult;
  }, [debouncedSave]);

  const cancelAutosave = useCallback(() => {
    debouncedSave.cancel();
  }, [debouncedSave]);

  return { cancelAutosave, captureBaseline, flushAutosave, hasUnsavedChanges, triggerAutosave };
};
